import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import ImageUploader from './ImageUploader';

// ---- Types
interface ProductFormData {
  name: string;
  product_code: string;
  description: string;
  long_description: string;
  price: string;
  original_price: string;
  moq: string;
  category: string;
  tag: string;
  is_featured: boolean;
  is_visible: boolean;
  sort_order: string;
  specs_material: string;
  specs_size: string;
  specs_weight: string;
}

interface HighlightItem {
  icon: string;
  title: string;
  desc: string;
}

interface FaqItem {
  q: string;
  a: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  long_description: string | null;
  price: number;
  original_price: number | null;
  category: string;
  image: string;
  images: string[] | null;
  tags: string[] | null;
  is_featured: boolean;
  sort_order: number;
  specs: Record<string, string> | null;
  highlights: HighlightItem[] | null;
  faqs: FaqItem[] | null;
  translations: Record<string, ProductTranslationData> | null;
}

interface ProductTranslationData {
  name?: string;
  description?: string;
  long_description?: string;
}

const LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'pt', label: 'Português' },
  { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' },
] as const;

const EMPTY_TRANSLATIONS: Record<string, ProductTranslationData> = {
  en: { name: '', description: '', long_description: '' },
  ja: { name: '', description: '', long_description: '' },
  'zh-TW': { name: '', description: '', long_description: '' },
  pt: { name: '', description: '', long_description: '' },
  es: { name: '', description: '', long_description: '' },
  nl: { name: '', description: '', long_description: '' },
};

interface ProductFormModalProps {
  product: Product | null;
  onClose: () => void;
  onSaved: () => void;
}

// ---- Constants
const ICON_OPTIONS = [
  'ri-star-line', 'ri-stack-line', 'ri-leaf-line', 'ri-shield-check-line',
  'ri-footprint-line', 'ri-plant-line', 'ri-home-3-line', 'ri-arrow-up-line',
  'ri-scissors-line', 'ri-refresh-line', 'ri-water-flash-line', 'ri-drop-line',
  'ri-sofa-line', 'ri-tools-line', 'ri-shield-line', 'ri-gift-line',
  'ri-circle-line', 'ri-safe-line', 'ri-building-line', 'ri-gamepad-line',
  'ri-home-smile-line', 'ri-heart-line', 'ri-fire-line', 'ri-flashlight-line',
  'ri-award-line', 'ri-medal-line', 'ri-thumb-up-line', 'ri-sun-line',
  'ri-sparkling-line', 'ri-rainbow-line', 'ri-palette-line', 'ri-brush-line',
  'ri-truck-line', 'ri-global-line', 'ri-recycle-line', 'ri-seedling-line',
  'ri-box-3-line', 'ri-rocket-line', 'ri-lightbulb-line', 'ri-eye-line',
];

const DEFAULT_CATEGORIES = ['瓦楞纸', '剑麻', '综合'];

const EMPTY_FORM: ProductFormData = {
  name: '', product_code: '', description: '', long_description: '',
  price: '', original_price: '', moq: '1', category: '',
  tag: '', is_featured: false, is_visible: true, sort_order: '0',
  specs_material: '', specs_size: '', specs_weight: '',
};

const isSupabaseConnected = (): boolean => {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const key = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
  return Boolean(url && key && url.startsWith('https://'));
};

export default function ProductFormModal({ product, onClose, onSaved }: ProductFormModalProps) {
  // Categories from DB
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  // Presets from DB
  const [presetHighlights, setPresetHighlights] = useState<HighlightItem[]>([]);
  const [presetFaqs, setPresetFaqs] = useState<FaqItem[]>([]);

  // Basic fields
  const [form, setForm] = useState<ProductFormData>(EMPTY_FORM);
  // Images
  const [coverImage, setCoverImage] = useState<string>('');
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  // Visual editors
  const [highlights, setHighlights] = useState<HighlightItem[]>([
    { icon: 'ri-star-line', title: '', desc: '' },
    { icon: 'ri-shield-check-line', title: '', desc: '' },
    { icon: 'ri-leaf-line', title: '', desc: '' },
    { icon: 'ri-footprint-line', title: '', desc: '' },
  ]);
  const [faqs, setFaqs] = useState<FaqItem[]>([
    { q: '', a: '' }, { q: '', a: '' }, { q: '', a: '' },
  ]);
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Multi-language translations
  const [translations, setTranslations] = useState<Record<string, ProductTranslationData>>(EMPTY_TRANSLATIONS);
  const [translationTab, setTranslationTab] = useState('ja');
  const [translationOpen, setTranslationOpen] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState<Set<string>>(new Set());

  // Load categories from DB
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    supabase
      .from('categories')
      .select('name')
      .order('sort_order', { ascending: true })
      .then(({ data, error: _err }) => {
        if (!_err && data && data.length > 0) {
          setCategories(data.map((d: { name: string }) => d.name));
        }
      });
    // Load highlight presets
    supabase
      .from('highlight_presets')
      .select('*')
      .order('sort_order', { ascending: true })
      .then(({ data, error: _err2 }) => {
        if (!_err2 && data) {
          setPresetHighlights(data as HighlightItem[]);
        }
      });
    // Load FAQ presets
    supabase
      .from('faq_presets')
      .select('*')
      .order('sort_order', { ascending: true })
      .then(({ data, error: _err3 }) => {
        if (!_err3 && data) {
          setPresetFaqs(data as FaqItem[]);
        }
      });
  }, []);

  // ---- Auto-translate via Edge Function ----
  const handleAutoTranslate = async (lang: string) => {
    if (!isSupabaseConnected()) {
      setError('请先连接 Supabase');
      return;
    }

    const texts: Record<string, string> = {};
    if (form.name.trim()) texts.name = form.name.trim();
    if (form.description.trim()) texts.description = form.description.trim();
    if (form.long_description.trim()) texts.long_description = form.long_description.trim();

    if (Object.keys(texts).length === 0) {
      setError('请先填写中文内容再翻译');
      return;
    }

    setAutoTranslating((prev) => new Set(prev).add(lang));
    setError('');

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('translate', {
        body: { texts, targetLang: lang },
      });

      if (fnErr) throw fnErr;

      const result = data?.translations as Record<string, string> | undefined;
      if (result) {
        setTranslations((prev) => ({
          ...prev,
          [lang]: {
            name: result.name ?? prev[lang]?.name ?? '',
            description: result.description ?? prev[lang]?.description ?? '',
            long_description: result.long_description ?? prev[lang]?.long_description ?? '',
          },
        }));
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '翻译失败，请重试';
      setError(msg);
    } finally {
      setAutoTranslating((prev) => {
        const next = new Set(prev);
        next.delete(lang);
        return next;
      });
    }
  };

  const handleAutoTranslateAll = async () => {
    const langs = LANG_OPTIONS.map((l) => l.code);
    for (const lang of langs) {
      await handleAutoTranslate(lang);
    }
  };

  // Populate form when editing — only depend on product, not categories
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (product) {
      setForm({
        name: product.name,
        product_code: (product as any).product_code ?? '',
        description: product.description ?? '',
        long_description: product.long_description ?? '',
        price: String(product.price),
        original_price: product.original_price != null ? String(product.original_price) : '',
        moq: product.moq != null ? String(product.moq) : '1',
        category: product.category ?? (DEFAULT_CATEGORIES[0]),
        tag: (product.tags && product.tags.length > 0) ? product.tags[0] : '',
        is_featured: product.is_featured ?? false,
        is_visible: product.is_visible ?? true,
        sort_order: String(product.sort_order ?? 0),
        specs_material: product.specs?.material ?? '',
        specs_size: product.specs?.size ?? '',
        specs_weight: product.specs?.weight ?? '',
      });
      setCoverImage(product.image ?? '');
      setGalleryImages(product.images ?? []);
      if (product.highlights && product.highlights.length > 0) {
        setHighlights(product.highlights);
      }
      if (product.faqs && product.faqs.length > 0) {
        setFaqs(product.faqs);
      }
      // Load existing translations
      if (product.translations) {
        setTranslations({
          en: { name: product.translations.en?.name || '', description: product.translations.en?.description || '', long_description: product.translations.en?.long_description || '' },
          ja: { name: product.translations.ja?.name || '', description: product.translations.ja?.description || '', long_description: product.translations.ja?.long_description || '' },
          'zh-TW': { name: product.translations['zh-TW']?.name || '', description: product.translations['zh-TW']?.description || '', long_description: product.translations['zh-TW']?.long_description || '' },
          pt: { name: product.translations.pt?.name || '', description: product.translations.pt?.description || '', long_description: product.translations.pt?.long_description || '' },
          es: { name: product.translations.es?.name || '', description: product.translations.es?.description || '', long_description: product.translations.es?.long_description || '' },
          nl: { name: product.translations.nl?.name || '', description: product.translations.nl?.description || '', long_description: product.translations.nl?.long_description || '' },
        });
      } else {
        setTranslations(EMPTY_TRANSLATIONS);
      }
    } else {
      setForm({ ...EMPTY_FORM, category: DEFAULT_CATEGORIES[0] });
      setCoverImage('');
      setGalleryImages([]);
      setHighlights([
        { icon: 'ri-star-line', title: '', desc: '' },
        { icon: 'ri-shield-check-line', title: '', desc: '' },
        { icon: 'ri-leaf-line', title: '', desc: '' },
        { icon: 'ri-footprint-line', title: '', desc: '' },
      ]);
      setFaqs([{ q: '', a: '' }, { q: '', a: '' }, { q: '', a: '' }]);
      setTranslations(EMPTY_TRANSLATIONS);
    }
  }, [product]);

  // Basic field change
  const handleChange = (field: keyof ProductFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Highlights helpers
  const updateHighlight = (index: number, field: keyof HighlightItem, value: string) => {
    setHighlights((prev) => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  };
  const addHighlight = () => {
    setHighlights((prev) => [...prev, { icon: 'ri-star-line', title: '', desc: '' }]);
  };
  const removeHighlight = (index: number) => {
    if (highlights.length <= 1) return;
    setHighlights((prev) => prev.filter((_, i) => i !== index));
  };

  // FAQs helpers
  const updateFaq = (index: number, field: keyof FaqItem, value: string) => {
    setFaqs((prev) => prev.map((f, i) => (i === index ? { ...f, [field]: value } : f)));
  };
  const addFaq = () => {
    setFaqs((prev) => [...prev, { q: '', a: '' }]);
  };
  const removeFaq = (index: number) => {
    if (faqs.length <= 1) return;
    setFaqs((prev) => prev.filter((_, i) => i !== index));
  };

  // Translation helpers
  const updateTranslation = (lang: string, field: keyof ProductTranslationData, value: string) => {
    setTranslations((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) { setError('请输入商品名称'); return; }
    if (!form.price || isNaN(Number(form.price))) { setError('请输入有效价格'); return; }

    const finalGallery = galleryImages.length > 0 ? galleryImages : (coverImage ? [coverImage] : []);

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      long_description: form.long_description.trim() || null,
      price: Number(form.price),
      original_price: form.original_price ? Number(form.original_price) : null,
      moq: form.moq ? parseInt(form.moq, 10) : 1,
      category: form.category,
      image: coverImage.trim(),
      images: finalGallery,
      tags: form.tag ? [form.tag] : [],
      product_code: form.product_code?.trim() || null,
      is_featured: form.is_featured,
      is_visible: form.is_visible,
      sort_order: Number(form.sort_order) || 0,
      specs: {
        material: form.specs_material.trim(),
        size: form.specs_size.trim(),
        weight: form.specs_weight.trim(),
        moq: form.moq ? parseInt(form.moq, 10) : 1,
      },
      highlights: highlights.filter((h) => h.title.trim() || h.desc.trim()),
      faqs: faqs.filter((f) => f.q.trim() && f.a.trim()),
      translations: {
        en: translations.en,
        ja: translations.ja,
        'zh-TW': translations['zh-TW'],
        pt: translations.pt,
        es: translations.es,
        nl: translations.nl,
      },
      updated_at: new Date().toISOString(),
    };

    setSaving(true);
    try {
      if (product) {
        const { data: updatedData, error: err } = await supabase.from('products').update(payload).eq('id', product.id).select('id').maybeSingle();
        if (err) throw err;
        if (!updatedData) {
          throw new Error('保存失败：权限不足，请重新登录后再试');
        }
      } else {
        const { data: insertedData, error: err } = await supabase.from('products').insert(payload).select('id').maybeSingle();
        if (err) throw err;
        if (!insertedData) {
          throw new Error('创建失败：权限不足，请重新登录后再试');
        }
      }
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // Computed preview
  const priceNum = parseFloat(form.price) || 0;
  const origPriceNum = parseFloat(form.original_price) || 0;
  const discount = origPriceNum > 0 ? Math.round((1 - priceNum / origPriceNum) * 100) : 0;
  const tagSelected = form.tag || '';

  // Section label component
  const SectionLabel = ({ title, subtitle }: { title: string; subtitle?: string }) => (
    <div className="flex items-center gap-3 pt-6 pb-3 border-t border-[#ede9e3] first:border-t-0 first:pt-0">
      <div className="w-1 h-5 rounded-full bg-[#C8714A] flex-shrink-0"></div>
      <div>
        <span className="text-sm font-semibold text-[#1a1a1a]">{title}</span>
        {subtitle && <span className="text-xs text-[#aaa] ml-2">{subtitle}</span>}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#ede9e3] flex-shrink-0">
          <h2 className="text-base font-semibold text-[#1a1a1a]">
            {product ? '编辑商品' : '新增商品'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-[#aaa] hover:text-[#333] rounded-lg hover:bg-[#f7f5f2] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Form body — single scrollable page */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5" id="product-form">

          {/* ===== 1. Category + Tags (matches front-end badge pills at top) ===== */}
          <SectionLabel title="分类与标签" subtitle="对应前台详情页顶部的品类标签和标签徽章" />

          <div className="flex gap-3 items-start mt-4">
            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-xs text-[#aaa]">商品分类</label>
              <select
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] focus:outline-none focus:border-[#C8714A] transition-colors bg-white cursor-pointer"
              >
                {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-xs text-[#aaa]">商品标签</label>
              <select
                value={form.tag}
                onChange={(e) => handleChange('tag', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] focus:outline-none focus:border-[#C8714A] transition-colors bg-white cursor-pointer"
              >
                <option value="">无标签</option>
                <option value="即将上市">即将上市</option>
                <option value="热销中">热销中</option>
                <option value="已下架">已下架</option>
              </select>
            </div>
          </div>

          {/* Preview badges */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {form.category && (
              <span className="text-xs text-[#C8714A] font-medium bg-[#C8714A]/10 px-2.5 py-1 rounded-full">
                {form.category}
              </span>
            )}
            {tagSelected && (
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                tagSelected === '热销中' ? 'text-red-600 bg-red-50' :
                tagSelected === '即将上市' ? 'text-blue-600 bg-blue-50' :
                'text-gray-500 bg-gray-100'
              }`}>
                {tagSelected}
              </span>
            )}
            {!form.category && !tagSelected && (
              <span className="text-xs text-[#ccc]">填写后此处显示前台标签效果</span>
            )}
          </div>

          {/* ===== 2. Product Name (matches h1) ===== */}
          <SectionLabel title="商品名称" subtitle="对应前台详情页主标题" />
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="请输入商品名称"
            className="w-full px-3 py-2.5 mt-3 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
          />

          {/* ===== 2.5 Product Code ===== */}
          <div className="mt-4">
            <label className="text-xs text-[#aaa]">产品代码</label>
            <input
              type="text"
              value={form.product_code || ''}
              onChange={(e) => handleChange('product_code', e.target.value)}
              placeholder="如：KP-001、CSB-L-42"
              className="w-full px-3 py-2.5 mt-1.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] font-mono placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
            />
          </div>

          {/* ===== 3. Description (short) ===== */}
          <SectionLabel title="商品简介" subtitle="对应前台详情页标题下方的简短介绍文字" />
          <textarea
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="简短描述，展示在商品列表卡片和详情页标题下方"
            rows={2}
            className="w-full px-3 py-2.5 mt-3 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors resize-none"
          />

          {/* ===== 4. Price + Original Price ===== */}
          <SectionLabel title="价格信息" subtitle="对应前台详情页价格行" />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-[#aaa]">售价 $ <span className="text-red-400">*</span></label>
              <input
                type="number" value={form.price} onChange={(e) => handleChange('price', e.target.value)}
                placeholder="0.00" min="0" step="0.01"
                className="w-full px-3 py-2.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-[#aaa]">原价 $</label>
              <input
                type="number" value={form.original_price} onChange={(e) => handleChange('original_price', e.target.value)}
                placeholder="0.00" min="0" step="0.01"
                className="w-full px-3 py-2.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
              />
            </div>
          </div>

          {/* Price preview */}
          {priceNum > 0 && (
            <div className="flex items-center gap-3 mt-3 py-3 px-4 rounded-xl bg-[#faf8f5] border border-[#f0ebe4]">
              <span className="text-xl font-bold text-[#C8714A]">${priceNum}</span>
              {origPriceNum > 0 && (
                <>
                  <span className="text-sm text-[#ccc] line-through">${origPriceNum}</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#C8714A] text-white text-xs font-bold">
                    省${Math.round(origPriceNum - priceNum)}
                  </span>
                  {discount > 0 && (
                    <span className="text-xs text-[#aaa] bg-[#f5f0eb] px-2 py-0.5 rounded-full">
                      -{discount}% OFF
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {/* ===== 5. Cover Image ===== */}
          <SectionLabel title="封面图片" subtitle="对应前台商品列表卡片和详情页主图" />
          <div className="mt-3">
            {coverImage ? (
              <div className="flex items-start gap-4">
                <div className="w-32 h-32 rounded-xl overflow-hidden border border-[#ede9e3] flex-shrink-0">
                  <img src={coverImage} alt="封面预览" className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => setCoverImage('')}
                    className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i> 移除封面
                  </button>
                  <span className="text-xs text-[#aaa] break-all max-w-[200px]">{coverImage}</span>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 w-32 h-32 rounded-xl border-2 border-dashed border-[#ddd] bg-[#faf9f7] hover:border-[#C8714A] hover:bg-[#C8714A]/5 transition-colors cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center text-[#ccc]">
                  <i className="ri-camera-line text-xl"></i>
                </div>
                <span className="text-xs text-[#aaa]">上传封面</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = async () => {
                      const base64Data = (reader.result as string).split(',')[1];
                      const supabaseUrl = (import.meta as Record<string, unknown>).env
                        ? (import.meta as { env: Record<string, string> }).env.VITE_PUBLIC_SUPABASE_URL
                        : 'https://fdxjebyoqhgkrbiofazf.supabase.co';
                      const res = await fetch(`${supabaseUrl}/functions/v1/product-upload`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ fileName: file.name, fileData: base64Data, contentType: file.type }),
                      });
                      const data = await res.json();
                      if (data.publicUrl) setCoverImage(data.publicUrl);
                      else alert('上传失败');
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
            )}

            {/* Select cover from gallery */}
            {galleryImages.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-[#aaa] mb-2 flex items-center gap-1.5">
                  <i className="ri-image-line"></i> 或从图库中选择封面
                </p>
                <div className="flex gap-2 flex-wrap">
                  {galleryImages.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCoverImage(img)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${
                        coverImage === img ? 'border-[#C8714A] ring-2 ring-[#C8714A]/20' : 'border-[#ede9e3] hover:border-[#C8714A]/40'
                      }`}
                    >
                      <img src={img} alt={`图库${i + 1}`} className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ===== 6. Gallery Images ===== */}
          <SectionLabel title="详情图库" subtitle="对应前台详情页左侧图片轮播" />
          <div className="mt-3">
            <ImageUploader
              images={galleryImages}
              onChange={setGalleryImages}
              max={8}
              label="详情页图库（商品展示页的图片轮播）"
            />
            {galleryImages.length === 0 && (
              <p className="text-xs text-[#aaa] mt-1">未上传图库时，将使用封面图作为默认展示</p>
            )}
          </div>

          {/* ===== 7. Highlights ===== */}
          <SectionLabel title="产品亮点" subtitle="对应前台详情页 2 列卡片网格" />
          <div className="flex items-center gap-3 mt-3">
            <button
              type="button"
              onClick={addHighlight}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C8714A]/10 text-[#C8714A] text-xs font-medium hover:bg-[#C8714A]/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> 添加亮点
            </button>
          </div>

          {/* Highlights preview */}
          {highlights.filter((h) => h.title || h.desc).length > 0 && (
            <div className="rounded-xl border border-[#ede9e3] bg-[#faf8f5] p-4 mt-3">
              <p className="text-xs text-[#aaa] mb-3 flex items-center gap-1.5">
                <i className="ri-eye-line"></i> 前台展示预览
              </p>
              <div className="grid grid-cols-2 gap-3">
                {highlights.filter((h) => h.title || h.desc).map((h, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8714A]/10 flex-shrink-0">
                      <i className={`${h.icon || 'ri-star-line'} text-[#C8714A] text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-[#1a1a1a]">{h.title || '(标题)'}</div>
                      <div className="text-xs text-[#999] mt-0.5 leading-relaxed">{h.desc || '(描述)'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Highlights editors */}
          <div className="flex flex-col gap-3 mt-3">
            {highlights.map((h, i) => (
              <div key={i} className="flex gap-3 p-4 rounded-xl border border-[#ede9e3] bg-white items-start">
                <div className="flex-1 flex flex-col gap-2">
                  {/* Preset dropdown */}
                  <div className="flex items-center gap-2">
                    <select
                      value=""
                      onChange={(e) => {
                        const idx = parseInt(e.target.value, 10);
                        if (idx >= 0 && idx < presetHighlights.length) {
                          const preset = presetHighlights[idx];
                          updateHighlight(i, 'icon', preset.icon);
                          updateHighlight(i, 'title', preset.title);
                          updateHighlight(i, 'desc', preset.desc);
                        }
                      }}
                      className="flex-1 px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#555] bg-white cursor-pointer focus:outline-none focus:border-[#C8714A] transition-colors"
                    >
                      <option value="">📋 选择预设亮点（{presetHighlights.length}条可选）...</option>
                      {presetHighlights.map((preset, pi) => (
                        <option key={pi} value={pi}>{preset.icon.replace('ri-', '').replace('-line', '')} · {preset.title} — {preset.desc}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeHighlight(i)}
                      disabled={highlights.length <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-[#ccc] hover:text-red-400 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                  {/* Icon + fields row */}
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C8714A]/10 flex-shrink-0">
                      <i className={`${h.icon || 'ri-star-line'} text-[#C8714A] text-base`}></i>
                    </div>
                    <select
                      value={h.icon}
                      onChange={(e) => updateHighlight(i, 'icon', e.target.value)}
                      className="w-32 px-1.5 py-2 rounded border border-[#e8e4de] text-xs text-[#555] bg-white cursor-pointer focus:outline-none focus:border-[#C8714A] flex-shrink-0"
                    >
                      {ICON_OPTIONS.map((ico) => (
                        <option key={ico} value={ico}>{ico.replace('ri-', '').replace('-line', '')}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={h.title}
                      onChange={(e) => updateHighlight(i, 'title', e.target.value)}
                      placeholder="亮点标题"
                      className="flex-1 px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
                    />
                  </div>
                  <input
                    type="text"
                    value={h.desc}
                    onChange={(e) => updateHighlight(i, 'desc', e.target.value)}
                    placeholder="亮点描述"
                    className="w-full px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* ===== 8. Long Description (tab "desc") ===== */}
          <SectionLabel title="详细描述" subtitle="对应前台详情页「商品描述」标签内容" />
          <textarea
            value={form.long_description}
            onChange={(e) => handleChange('long_description', e.target.value)}
            placeholder="完整产品介绍，支持换行"
            rows={5}
            className="w-full px-3 py-2.5 mt-3 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors resize-none"
          />

          {/* ===== 9. Specs (tab "specs") ===== */}
          <SectionLabel title="规格参数" subtitle="对应前台详情页「规格参数」标签内的表格" />
          <div className="rounded-xl border border-[#ede9e3] overflow-hidden mt-3">
            {[
              { label: '材质', field: 'specs_material' as keyof ProductFormData, placeholder: '如：高密度瓦楞纸' },
              { label: '尺寸', field: 'specs_size' as keyof ProductFormData, placeholder: '如：45×30×8cm' },
              { label: '重量', field: 'specs_weight' as keyof ProductFormData, placeholder: '如：680g' },
              { label: 'MOQ', field: 'moq' as keyof ProductFormData, placeholder: '如：50', isNumber: true },
            ].map((row, idx) => (
              <div key={row.field} className={`flex items-center gap-4 px-4 py-3 ${idx % 2 === 0 ? 'bg-[#faf8f5]' : 'bg-white'} border-b border-[#f0ebe4] last:border-0`}>
                <span className="w-12 text-xs text-[#999] flex-shrink-0">{row.label}</span>
                <input
                  type={row.isNumber ? 'number' : 'text'}
                  value={form[row.field]}
                  onChange={(e) => handleChange(row.field, e.target.value)}
                  placeholder={row.placeholder}
                  min={row.isNumber ? 1 : undefined}
                  className="flex-1 px-0 py-0 bg-transparent text-sm text-[#1a1a1a] placeholder-[#ccc] focus:outline-none border-0"
                />
              </div>
            ))}
          </div>

          {/* ===== 10. FAQs ===== */}
          <SectionLabel title="常见问题" subtitle="对应前台详情页「常见问题」标签内的手风琴折叠" />
          <div className="flex items-center gap-3 mt-3">
            <button
              type="button"
              onClick={addFaq}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C8714A]/10 text-[#C8714A] text-xs font-medium hover:bg-[#C8714A]/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> 添加问题
            </button>
          </div>

          {/* FAQ preview */}
          {faqs.filter((f) => f.q && f.a).length > 0 && (
            <div className="rounded-xl border border-[#ede9e3] bg-[#faf8f5] p-4 mt-3">
              <p className="text-xs text-[#aaa] mb-3 flex items-center gap-1.5">
                <i className="ri-eye-line"></i> 前台展示预览
              </p>
              <div className="flex flex-col gap-2">
                {faqs.filter((f) => f.q && f.a).map((f, i) => (
                  <div key={i} className="rounded-xl border border-[#f0ebe4] bg-white overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm font-medium text-[#1a1a1a]">{f.q}</span>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-[#C8714A]"></i>
                      </div>
                    </div>
                    <div className="px-4 pb-4 pt-3 border-t border-[#f0ebe4] bg-[#faf8f5]">
                      <span className="text-sm text-[#666] leading-relaxed">{f.a}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ editors */}
          <div className="flex flex-col gap-3 mt-3">
            {faqs.map((f, i) => (
              <div key={i} className="flex gap-3 p-4 rounded-xl border border-[#ede9e3] bg-white items-start">
                <div className="flex-1 flex flex-col gap-2">
                  {/* Preset dropdown */}
                  <div className="flex items-center gap-2">
                    <select
                      value=""
                      onChange={(e) => {
                        const idx = parseInt(e.target.value, 10);
                        if (idx >= 0 && idx < presetFaqs.length) {
                          const preset = presetFaqs[idx];
                          updateFaq(i, 'q', preset.q);
                          updateFaq(i, 'a', preset.a);
                        }
                      }}
                      className="flex-1 px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#555] bg-white cursor-pointer focus:outline-none focus:border-[#C8714A] transition-colors"
                    >
                      <option value="">📋 选择预设问题（{presetFaqs.length}条可选）...</option>
                      {presetFaqs.map((preset, pi) => (
                        <option key={pi} value={pi}>{preset.q}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeFaq(i)}
                      disabled={faqs.length <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-[#ccc] hover:text-red-400 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                  {/* Q + A fields */}
                  <input
                    type="text"
                    value={f.q}
                    onChange={(e) => updateFaq(i, 'q', e.target.value)}
                    placeholder="问题（如：猫薄荷的香味能持续多久？）"
                    className="w-full px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
                  />
                  <textarea
                    value={f.a}
                    onChange={(e) => updateFaq(i, 'a', e.target.value)}
                    placeholder="回答内容"
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors resize-none"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* ===== 11. Multi-language Translations ===== */}
          <SectionLabel title="多语言翻译" subtitle="为英语、日语、繁体中文、葡萄牙语、西班牙语、荷兰语填写翻译内容" />
          <div className="mt-3">
            {/* Toggle button */}
            <button
              type="button"
              onClick={() => setTranslationOpen(!translationOpen)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#e8e4de] text-sm text-[#555] hover:bg-[#faf9f7] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-translate text-[#C8714A] transition-transform ${translationOpen ? 'rotate-90' : ''}`}></i>
              </div>
              <span>{translationOpen ? '收起翻译面板' : '展开翻译面板'}</span>
              <span className="text-xs text-[#aaa]">（点「自动翻译」或「一键翻译全部」调用 API 真实翻译）</span>
            </button>

            {translationOpen && (
              <div className="mt-3 rounded-xl border border-[#ede9e3] overflow-hidden">
                {/* Auto-translate all banner */}
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#C8714A]/5 to-[#C8714A]/10 border-b border-[#ede9e3]">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#C8714A]/20">
                      <i className="ri-global-line text-[#C8714A] text-xs"></i>
                    </div>
                    <span className="text-xs text-[#555]">
                      一键将中文内容翻译为六种语言
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleAutoTranslateAll}
                    disabled={autoTranslating.size > 0 || !form.name.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#C8714A] text-white text-xs font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {autoTranslating.size > 0 ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        翻译中...
                      </>
                    ) : (
                      <>
                        <i className="ri-translate-2"></i>
                        一键翻译全部
                      </>
                    )}
                  </button>
                </div>

                {/* Language tabs */}
                <div className="flex border-b border-[#ede9e3] bg-[#faf9f7]">
                  {LANG_OPTIONS.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => setTranslationTab(lang.code)}
                      className={`flex-1 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        translationTab === lang.code
                          ? 'bg-white text-[#C8714A] border-b-2 border-[#C8714A]'
                          : 'text-[#888] hover:text-[#555]'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>

                {/* Translation fields */}
                <div className="p-4 bg-white">
                  {/* Action buttons */}
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <span className="text-xs text-[#aaa]">
                      当前语言：<span className="font-medium text-[#C8714A]">
                        {LANG_OPTIONS.find((l) => l.code === translationTab)?.label}
                      </span>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setTranslations((prev) => ({
                            ...prev,
                            [translationTab]: {
                              name: form.name,
                              description: form.description,
                              long_description: form.long_description,
                            },
                          }));
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#e8e4de] text-[#888] text-xs hover:bg-[#f7f5f2] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-copy-line"></i>
                        从中文复制
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAutoTranslate(translationTab)}
                        disabled={autoTranslating.has(translationTab) || !form.name.trim()}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#C8714A] text-white text-xs font-medium hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {autoTranslating.has(translationTab) ? (
                          <>
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            翻译中...
                          </>
                        ) : (
                          <>
                            <i className="ri-translate-2"></i>
                            自动翻译
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-xs text-[#aaa]">商品名称</label>
                      <input
                        type="text"
                        value={translations[translationTab]?.name || ''}
                        onChange={(e) => updateTranslation(translationTab, 'name', e.target.value)}
                        placeholder="翻译后的商品名称"
                        className="w-full px-3 py-2.5 mt-1.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#aaa]">商品简介</label>
                      <textarea
                        value={translations[translationTab]?.description || ''}
                        onChange={(e) => updateTranslation(translationTab, 'description', e.target.value)}
                        placeholder="翻译后的简短描述"
                        rows={2}
                        className="w-full px-3 py-2.5 mt-1.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors resize-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#aaa]">详细描述</label>
                      <textarea
                        value={translations[translationTab]?.long_description || ''}
                        onChange={(e) => updateTranslation(translationTab, 'long_description', e.target.value)}
                        placeholder="翻译后的完整产品描述"
                        rows={4}
                        className="w-full px-3 py-2.5 mt-1.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ===== 12. Featured + Visibility + Sort ===== */}
          <SectionLabel title="其他设置" />
          <div className="grid grid-cols-2 gap-4 mt-3">
            <label className="flex items-center gap-3 cursor-pointer select-none p-4 rounded-xl bg-[#faf8f5] border border-[#ede9e3]">
              <div
                onClick={() => handleChange('is_featured', !form.is_featured)}
                className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative cursor-pointer ${form.is_featured ? 'bg-[#C8714A]' : 'bg-[#ddd]'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_featured ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <div>
                <div className="text-sm text-[#333]">首页推荐展示</div>
                <div className="text-xs text-[#aaa]">开启后在首页「热门产品」区域展示</div>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer select-none p-4 rounded-xl bg-[#faf8f5] border border-[#ede9e3]">
              <div
                onClick={() => handleChange('is_visible', !form.is_visible)}
                className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative cursor-pointer ${form.is_visible ? 'bg-[#C8714A]' : 'bg-[#ddd]'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_visible ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <div>
                <div className="text-sm text-[#333]">产品列表显示</div>
                <div className="text-xs text-[#aaa]">关闭后该商品不在前台产品列表中显示</div>
              </div>
            </label>
          </div>

          <div className="mt-4">
            <div className="flex flex-col gap-1.5 p-4 rounded-xl bg-[#faf8f5] border border-[#ede9e3]">
              <label className="text-xs text-[#aaa]">排序权重（数字越大越靠前）</label>
              <input
                type="number" value={form.sort_order} onChange={(e) => handleChange('sort_order', e.target.value)}
                placeholder="0" min="0"
                className="w-full px-3 py-2.5 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-5 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              {error}
            </div>
          )}

          {/* Spacer */}
          <div className="h-4" />
        </form>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-[#ede9e3] flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-[#e8e4de] text-sm text-[#555] hover:bg-[#f7f5f2] transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            type="submit"
            form="product-form"
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-[#C8714A] text-white text-sm font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                保存中...
              </>
            ) : (
              <>{product ? '保存修改' : '创建商品'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}